import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef, MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {PeoClientApiModel} from '../../../../model/peo-client-api.model';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {PeoClientService} from '../../../../service/peo-client.service';
import {ActivatedRoute} from '@angular/router';
import {DatePipe, NgIf} from '@angular/common';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {UtilityService} from '../../../../service/utility.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {PeoClientAdminDialogComponent} from '../../../dialog/admin/peo-client/peo-client.component';
import {ConfirmationDialogComponent} from '../../../dialog/confirmation/confirmation.component';
import {Observable} from 'rxjs';
import {PeoClientApiManyRequestModel} from '../../../../model/peo-client-api-many-request.model';
import {CustomerService} from '../../../../service/customer.service';
import {
  PeoClientDocumentSplitStatusAdminDialogComponent
} from "../../../dialog/admin/peo-client-document-split-status/peo-client-document-split-status.component";

/**
 * @class
 * @component
 * @description This class maintains the Administrator PEO Clients page component of our application.
 * @export
 * @name PeoClientAdminPageComponent
 */
@Component({
  imports: [
    DatePipe,
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFormField,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatPaginator,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatSuffix,
    MatTable,
    MatTooltip,
    NgIf,
    ReactiveFormsModule,
    FormsModule,
    MatHeaderCellDef,
    MatNoDataRow,
    MatProgressSpinner
  ],
  selector: 'app-page-admin-peo-client',
  standalone: true,
  styleUrl: './peo-client.component.scss',
  templateUrl: './peo-client.component.html'
})
export class PeoClientAdminPageComponent implements AfterViewInit {

  /**
   * @description This property contains the columns for the PEO Clients table that can be filtered.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#filterColumns
   * @property
   * @protected
   * @readonly
   * @type {(keyof PeoClientApiModel)[]}
   */
  protected readonly filterColumns: (keyof PeoClientApiModel)[] =
    ['peoId', 'clientId', 'customerId', 'customerName', 'customerBrandName', 'printSetup'];

  /**
   * @description This property contains the filter text for the PEOs table.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#filterInput
   * @property
   * @protected
   * @type {string}
   */
  protected filterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the loading state of the clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @description This property contains the PEO Clients registered in the system.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#clientDataSource
   * @property
   * @protected
   * @type {MatTableDataSource<PeoClientApiModel>}
   */
  protected readonly clientDataSource: MatTableDataSource<PeoClientApiModel> =
    new MatTableDataSource<PeoClientApiModel>();

  /**
   * @default {}
   * @description This property contains the toggles for the clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#clientToggles
   * @property
   * @protected
   * @type {{[key: string]: {
   *    error?: Error,
   *    loaded: boolean,
   *    loading: boolean,
   *    saved: boolean,
   *    saving: boolean
   *  }}
   */
  protected clientToggles: {
    [key: string]: {
      error?: Error,
      loaded: boolean,
      loading: boolean,
      saved: boolean,
      saving: boolean
    }
  } = {};

  /**
   * @description This property contains the unique identifier for which to load the PEO Clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#peoId
   * @property
   * @protected
   * @type {string?}
   */
  protected peoId?: string;

  /**
   * @default false
   * @description This property contains the loading state of the print settings for the clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#printSettingsLoading
   * @property
   * @protected
   * @type {boolean}
   */
  protected printSettingsLoading: boolean = false;

  /**
   * @default false
   * @description This property contains the saving state of the print settings for the clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#printSettingsSaving
   * @property
   * @protected
   * @type {boolean}
   */
  protected printSettingsSaving: boolean = false;

  /**
   * @default false
   * @description This property contains the saving state for the clients.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#saving
   * @property
   * @protected
   * @type {boolean}
   */
  protected saving: boolean = false;

  /**
   * @description This property contains the columns for the PEO Clients table.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#tableColumns
   * @property
   * @protected
   * @readonly
   * @type {string[]}
   */
  protected readonly tableColumns: string[] = [
    'peoId', 'clientId', 'customerId', 'customerName', 'customerBrand',
    'lastPreviewed', 'lastPrinted', 'printSetup', '_actions'
  ];

  /**
   * @description This property contains the paginator for the PEO Clients table.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#paginator
   * @property
   * @protected
   * @readonly
   * @type {MatPaginator}
   */
  @ViewChild(MatPaginator)
  protected readonly paginator!: MatPaginator;

  /**
   * @description This property contains the sorter for the PEO Clients table.
   * @memberof PeoClientAdminPageComponent
   * @name PeoClientAdminPageComponent#sort
   * @property
   * @protected
   * @readonly
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected readonly sort!: MatSort;

  /**
   * @constructor
   * @description This method instantiates a new instance of our peo clients page component with injected dependencies.
   * @param {CustomerService} customerService The injected instance of our customer service provider.
   * @param {MatDialog} dialog The injected instance of the Angular Material dialog service.
   * @param {ActivatedRoute} route The injected instance of our activated route.
   * @param {PeoService} service The injected instance of our PrismHR PEO service provider.
   * @param {MatSnackBar} snackbar The injected instance of the Angular Material snackbar service.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#constructor
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our customer service provider.
     * @memberof PeoClientAdminPageComponent
     * @name PeoClientAdminPageComponent#customerService
     * @private
     * @property
     * @readonly
     * @type {CustomerService}
     */
    private readonly customerService: CustomerService,
    /**
     * @description This property contains the Angular Material dialog service.
     * @memberof PeoAdminPageComponent
     * @name PeoAdminPageComponent#dialog
     * @private
     * @property
     * @readonly
     * @type {MatDialog}
     */
    private readonly dialog: MatDialog,
    /**
     * @description This property contains the injected instance of our activated route.
     * @memberof PeoClientAdminPageComponent
     * @name PeoClientAdminPageComponent#service
     * @private
     * @property
     * @readonly
     * @type {ActivatedRoute}
     */
    private readonly route: ActivatedRoute,
    /**
     * @description This property contains the injected instance of our PrismHR Employee service provider.
     * @memberof PeoClientAdminPageComponent
     * @name PeoClientAdminPageComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly service: PeoClientService,
    /**
     * @description This property contains the Angular Material snackbar service.
     * @memberof PeoAdminPageComponent
     * @name PeoAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {

    // Add our custom table filter predicate.
    this.clientDataSource.filterPredicate =
      UtilityService.TableMultiFilterPredicate<PeoClientApiModel>(this.filterColumns as (keyof PeoClientApiModel)[]);
  }

  /**
   * @description This method checks the print toggles for the save state.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#checkClientToggles()
   * @param {('load' | 'print-load' | 'print-save' | 'save')} key This parameter contains the key to check the client toggles for.
   * @private
   * @returns {void}
   */
  private checkClientToggles(key: ('load' | 'save')): void {

    // Check the key for loading then check the toggles to reset the instance state.
    if (key === 'load') this.loading = !Object.keys(this.clientToggles).every((k: string): boolean =>
      this.clientToggles[k].loaded);

    // Check the key for saving then check the toggles to reset the instance state.
    if (key === 'save') this.saving = !Object.keys(this.clientToggles).every((k: string): boolean =>
      this.clientToggles[k].saved || !!this.clientToggles[k].error);
  }

  /**
   * @description This method creates the client print setting toggles for each client loaded.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#createClientToggles()
   * @param {PeoClientApiModel} [client] This parameter contains the client to create the toggles for.
   * @private
   * @returns {void}
   */
  private createClientToggles(client?: PeoClientApiModel): void {

    // Check for a provided client.
    if (!!client) {

      // Set the print settings loaded state.
      this.clientToggles[this.getToggleKey(client)] = {

        // Set the print settings loaded state.
        loaded: false,

        // Set the print settings loading state.
        loading: false,

        // Set the print settings saved state.
        saved: false,

        // Set the print settings saving state.
        saving: false
      };

      // Return the method.
      return;
    }

    // Iterate over the clients and generate their toggle states.
    this.clientDataSource.data.forEach((c: PeoClientApiModel) =>
      this.clientToggles[this.getToggleKey(c)] = {

        // Set the print settings loaded state.
        loaded: false,

        // Set the print settings loading state.
        loading: false,

        // Set the print settings saved state.
        saved: false,

        // Set the print settings saving state.
        saving: false
      });
  }

  /**
   * @description This method handles the print settings saving for the client.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#runSavePrintSettings()
   * @param {boolean} [revert] This parameter contains the flag to revert the print settings.
   * @param {PeoClientApiModel} [client] This parameter contains the client to save the print settings for.
   * @private
   * @returns {void}
   */
  private runSavePrintSettings(revert: boolean, client?: PeoClientApiModel): void {

    // Check for a provided client.
    if (!!client) {

      // Set the print settings saving state.
      this.updateSavingClientToggles(client, undefined, false, true);

      // Save the print settings for the client.
      const observable: Observable<any> = revert
        ? this.service.revertPrintSettingsForCustomer(client.peoId, client.clientId)
        : this.service.savePrintSettingsForCustomer(client.peoId, client.clientId);

      // Subscribe to the observable.
      observable.subscribe({

        // Handle the completion event.
        complete: (): void => {

          // Set the print settings saving state.
          this.updateSavingClientToggles(client, undefined, true, false);

          // Reload the print settings for the client.
          this.loadClient(client);
        },

        // Handle any errors that occur.
        error: (e: Error): MatSnackBarRef<TextOnlySnackBar> => {

          // Set the print settings saving state.
          this.updateSavingClientToggles(client, e, false, false);

          // Return the snack bar.
          return this.snackbar.open(`${revert ? 'Reverting' : 'Saving'} PEO Client-Print Settings failed with:  ${e.message}`,
            'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom'
            });
        }
      });

      // We're done here, so return.
      return;
    }

    // Iterate over the clients and update their print settings saving state toggles.
    this.clientDataSource.data.forEach((c: PeoClientApiModel): void =>
      this.updateSavingClientToggles(c, undefined, false, true));

    // Localize our list of print settings to save.
    const printSettings: PeoClientApiManyRequestModel[] =
      this.clientDataSource.data.map((c: PeoClientApiModel): PeoClientApiManyRequestModel =>
        PeoClientApiManyRequestModel.Factory(c.peoId, c.clientId));

    // Revert or save the print settings for the clients.
    (revert
      ? this.service.revertManyPrintSettingsForCustomer(printSettings)
      : this.service.saveManyPrintSettingsForCustomer(printSettings)).subscribe({

      // Handle any errors that occur.
      error: (e: Error): void => {

        // Set the print settings saving state on all clients.
        this.clientDataSource.data.forEach((c: PeoClientApiModel): void =>
          this.updateSavingClientToggles(c, e, false, false));

        // Send the snack bar notification to update the user.
        this.snackbar.open(`${revert ? 'Reverting' : 'Saving'} PEO Client-Print Settings failed with:  ${e.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });
      },

      // Handle the response from the service.
      next: (p: PeoClientApiModel[]): void => p.forEach((c: PeoClientApiModel): void => {

        // Update the object in the data source.
        this.updateClientObject(c);

        // Update the toggles for the client.
        this.updateSavingClientToggles(c, undefined, true, false)
      })
    });
  }

  /**
   * @description This method updates the client object in the data source.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#updateClientObject()
   * @param {PeoClientApiModel} client This parameter contains the client to update.
   * @private
   * @returns {void}
   */
  private updateClientObject(client: PeoClientApiModel): void {

    // Determine the print setup of the incoming client.
    client = PeoClientApiModel.DetermineSetup(client);

    // Localize the index of the matching client in the data source.
    const index: number = this.clientDataSource.data.findIndex((c: PeoClientApiModel): boolean =>
      c.peoId === client.peoId && c.clientId === client.clientId && c.customerId === client.customerId);

    // Ensure we have an index before continuing.
    if (index === -1) return;

    // Localize the data source.
    const dataSource: PeoClientApiModel[] = this.clientDataSource.data;

    // Reset the client in the data source.
    dataSource[index] = client;

    // Update the data source in the instance.
    this.clientDataSource.data = [...dataSource];
  }

  /**
   * @description This method updates the client toggles for the loading workflow.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#updateLoadingClientToggles()
   * @param {PeoClientApiModel} client This parameter contains the client to update the toggles for.
   * @param {boolean?} [loaded] This parameter contains the loaded state.
   * @param {boolean?} [loading] This parameter contains the loading state.
   * @private
   * @returns {void}
   */
  private updateLoadingClientToggles(client: PeoClientApiModel, loaded?: boolean, loading?: boolean): void {

    // Localize the key for the client toggles.
    const key: string = this.getToggleKey(client);

    // Reset the loaded state into the client toggles.
    this.clientToggles[key].loaded = loaded ?? this.clientToggles[key].loaded;

    // Reset the loading state into the client toggles.
    this.clientToggles[key].loading = loading ?? this.clientToggles[key].loading;

    // Check the toggles for the loading state.
    this.checkClientToggles('load');
  }

  /**
   * @description This method updates the client toggles for the saving workflow.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#updateSavingClientToggles()
   * @param {PeoClientApiModel} client This parameter contains the client to update the toggles for.
   * @param {Error} [error] This parameter contains the error state.
   * @param {boolean} [saved] This parameter contains the saved state.
   * @param {boolean} [saving] This parameter contains the saving state.
   * @private
   * @returns {void}
   */
  private updateSavingClientToggles(client: PeoClientApiModel, error?: Error, saved?: boolean, saving?: boolean): void {

    // Localize the key for the client toggles.
    const key: string = this.getToggleKey(client);

    // Reset the error state into the client toggles.
    this.clientToggles[key].error = error ?? this.clientToggles[key].error;

    // Reset the saved state into the client toggles.
    this.clientToggles[key].saved = saved ?? this.clientToggles[key].saved;

    // Reset the saving state into the client toggles.
    this.clientToggles[key].saving = saving ?? this.clientToggles[key].saving;

    // Check the for the loading state.
    this.checkClientToggles('save');
  }

  /**
   * @description This method clears the filter on the PEO Clients table.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#clearFilter()
   * @protected
   * @returns {void}
   */
  protected clearFilter(): void {

    // Clear the filter input.
    this.filterInput = UtilityService.EmptyString;

    // Clear the filter on the PEOs in the instance.
    this.clientDataSource.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    this.clientDataSource.paginator?.firstPage();
  }

  /**
   * @description This method presents the confirmation dialog for deleting a PEO Client.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#deleteClient()
   * @param {PeoClientApiModel} client The PEO Client to delete.
   * @protected
   * @returns {void}
   */
  protected deleteClient(client: PeoClientApiModel): void {

    // Localize our confirmation dialog reference.
    this.dialog.open(ConfirmationDialogComponent, {

      // Define the data to be passed to the dialog.
      data: {

        // Set the message of the dialog.
        message: `Are you sure you want to delete the PEO Client for ${client.customerName} with a PEO ID of ${client.peoId} and Client ID of ${client.clientId}?`,

        // Set the title of the dialog.
        title: 'Confirm Deletion of PEO Client-Customer Mapping'
      },

      // Define the width of the dialog.
      width: '480px'
    }).afterClosed().subscribe((confirmed: boolean): void => {

      // Ensure we have confirmation.
      if (!confirmed) return;

      // Reset the loading state into the instance.
      this.loading = true;

      // Delete the PEO Client.
      this.service.delete(client.peoId, client.clientId).subscribe({

        // Handle the completion event.
        complete: (): void => {

          // Update the data source in the instance.
          this.clientDataSource.data = this.clientDataSource.data.filter((c: PeoClientApiModel): boolean =>
            c.peoId === client.peoId && c.clientId !== client.clientId)
            .sort((a: PeoClientApiModel, b: PeoClientApiModel): number =>
              a.peoId.localeCompare(b.peoId) + a.clientId.localeCompare(b.clientId));

          // Set the paginator for the PEOs table.
          this.clientDataSource.paginator = this.paginator;

          // Set the sorter for the PEOs table.
          this.clientDataSource.sort = this.sort;

          // Reset the paginator to the first page if it exists.
          this.clientDataSource.paginator?.firstPage();

          // Send the snack bar notification to the user.
          this.snackbar.open(`PEO Client-Customer Mapping for ${client.customerName} with PEO ID of ${client.peoId} and Client ID of ${client.clientId} has been deleted.`,
            'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-success'],
              verticalPosition: 'bottom'
            });

          // Reset the loading state into the instance.
          this.loading = false;
        },

        // Handle any errors that occur by sending a snack bar notification to the user.
        error: (e: Error): void => {

          // Reset the loading state into the instance.
          this.loading = false;

          // Send the snack bar notification to the user.
          this.snackbar.open(`Failed to Delete PEO Client with:  ${e.message}`,
            'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom'
            })
        }
      });
    });
  }

  /**
   * @description This method filters the PEO Clients in the table.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#filterTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterTable(event: Event): void {

    // Reset the filter input.
    this.filterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.filterInput) return this.clearFilter();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.filterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.clientDataSource.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    if (this.clientDataSource.paginator) this.clientDataSource.paginator.firstPage();
  }

  /**
   * @description This method generates the print settings key for the client.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#getToggleKey()
   * @param {PeoClientApiModel} client This parameter contains the client to generate the print settings key for.
   * @protected
   * @returns {string}
   */
  protected getToggleKey(client: PeoClientApiModel): string {

    // Return the print settings key.
    return `${client.peoId}:${client.clientId}`;
  }

  /**
   * @description This method loads the print settings for the clients.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#loadClient()
   * @param {PeoClientApiModel} client This parameter contains the client to load the print settings for.
   * @protected
   * @returns {void}
   */
  protected loadClient(client: PeoClientApiModel): void {

    // Create the client toggles.
    this.createClientToggles(client);

    // Set the loading state.
    this.updateLoadingClientToggles(client, false, true);

    // Load the client back from the service.
    this.service.load(client.peoId, client.clientId).subscribe({

      // Handle any errors that occur.
      error: (e: Error): void => {

        // Reset the loading state for the client.
        this.updateLoadingClientToggles(client, false, false);

        // Send the snack bar notification to update the user.
        this.snackbar.open(`Loading PEO Client-Customer failed with:  ${e.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });
      },

      // Handle the response from the service.
      next: (p: PeoClientApiModel | null): void => {

        // Ensure we have a model to work with.
        if (!p) {

          // Send the snack bar notification to update the user.
          this.snackbar.open(`PEO Client-Customer not found for PEO ID: ${client.peoId} and Client ID: ${client.clientId}`,
            'Dismiss', {
              duration: 5000,
              horizontalPosition: 'end',
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom'
            });

          // We're done, reset the loading state for the client and return.
          return this.updateLoadingClientToggles(client, false, false);
        }

        // Otherwise, update the client object in the data source.
        this.updateClientObject(p);

        // Reset the loading state for the client.
        this.updateLoadingClientToggles(client, true, false);
      }
    });
  }

  /**
   * @description This method loads the PEO Clients.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#loadClients()
   * @protected
   * @returns {void}
   */
  protected loadClients(): void {

    // Reset the loading flag.
    this.loading = true;

    // Now we'll want to list the PEO clients.
    this.service.list(this.peoId).subscribe({

      // Handle any errors that occur.
      error: (e: Error): void => {

        // Reset the loading state in the instance.
        this.loading = false

        // Send the snack bar notification to update the user.
        this.snackbar.open(`Loading PEO Client-Customers failes with:  ${e.message}`,
          'Dismiss', {
            duration: 5000,
            horizontalPosition: 'end',
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom'
          });
      },

      // Handle the next event.
      next: (p: PeoClientApiModel[]): void => {

        // Localize the data source.
        const dataSource: PeoClientApiModel[] = this.clientDataSource.data;

        // Iterate over the incoming clients and replace them in our local data source.
        p.forEach((c: PeoClientApiModel): void => {

          // Determine the print setup of the incoming client.
          c = PeoClientApiModel.DetermineSetup(c);

          // Localize the index of the matching client in the data source.
          const index: number = dataSource.findIndex((d: PeoClientApiModel): boolean =>
            d.peoId === c.peoId && d.clientId === c.clientId && d.customerId === c.customerId);

          // Ensure we have an index before continuing.
          if (index === -1) dataSource.push(c);

          // Otherwise, reset the client in the data source.
          else dataSource[index] = c;
        });

        // Update the data source in the instance.
        this.clientDataSource.data = [...dataSource.sort((a: PeoClientApiModel, b: PeoClientApiModel): number =>
          a.peoId.localeCompare(b.peoId) + a.clientId.localeCompare(b.clientId))];

        // Create our client toggles.
        this.createClientToggles();

        // Set the paginator for the PEOs table.
        this.clientDataSource.paginator = this.paginator;

        // Set the sorter for the PEOs table.
        this.clientDataSource.sort = this.sort;

        // Reset the paginator to the first page if it exists.
        this.clientDataSource.paginator?.firstPage();

        // Reset the loading state in the instance.
        this.loading = false;
      }
    });
  }

  /**
   * @description This method opens the dialog to create a new PEO Client-Customer Mapping or modify an existing one.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#modifyClient()
   * @param {PeoClientApiModel?} [client] The PEO Client to modify.
   * @protected
   * @returns {void}
   */
  protected modifyClient(client?: PeoClientApiModel): void {

    // Open the dialog for the PEO.
    this.dialog.open(PeoClientAdminDialogComponent, {

      // Pass the data to the dialog.
      data: {

        // Pass the new client-customer mapping flag to the dialog.
        isNew: (!client || !client?.clientId),

        // Pass the client-customer mapping to the dialog.
        client,

        // Pass the PEO ID to the dialog.
        peoId: client?.peoId ?? this.peoId
      },

      // Disable the backdrop click.
      disableClose: true,

      // Set the width of the dialog.
      width: '1024px'
    }).afterClosed().subscribe((c?: PeoClientApiModel): void => {

      // Reload the PEO list when the dialog is closed and a PEO Client is returned.
      if (!!c) this.loadClients();
    });
  }

  /**
   * @description This method saves the print settings for the client.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#savePrintSettings()
   * @param {PeoClientApiModel} client This parameter contains the client to save the print settings for.
   * @param {boolean} [revert] This parameter contains the flag to revert the print settings.
   * @protected
   * @returns {void}
   */
  protected savePrintSettings(client: PeoClientApiModel, revert: boolean = false): void {

    // Open the confirmation dialog to confirm the print settings change.
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        cancelText: 'Cancel',
        confirmText: revert ? 'I Understand, Revert Settings to Standard' : 'I Understand, Save PrismHR Settings',
        title: ((revert ? 'Revert Print Settings Confirmation' : 'PrismHR Print Settings Confirmation')
          + '&nbsp;&nbsp;&lpar;PeoId: ' + client.peoId + ', ClientId: ' + client.clientId + '&rpar;'),
        message: ((revert
            ? 'Are you sure you want to <strong>revert</strong> the print settings back to <strong>standard</strong> for this client-customer?'
            : 'Are you sure you want to <strong>convert</strong> the print settings to <strong>PrismHR</strong> for this client-customer?')
          + '<br>This will alter the way Customers\' print documents are generated and delivered.')
      },

      disableClose: true
    }).afterClosed().subscribe((confirmed: boolean): void => {

      // Check for confirmation then save the print settings.
      if (confirmed) this.runSavePrintSettings(revert, client);
    });
  }

  /**
   * @description This method saves the print settings for all clients.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#savePrintSettingsForAllClients()
   * @param {boolean} [revert] This parameter contains the flag to revert the print settings.
   * @protected
   * @returns {void}
   */
  protected savePrintSettingsForAllClients(revert: boolean = false): void {

    // Open the confirmation dialog to confirm the print settings change.
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        cancelText: 'Cancel',
        confirmText: revert ? 'I Understand, Revert Settings to Standard' : 'I Understand, Save PrismHR Settings',
        title: revert ? 'Revert Print Settings Confirmation' : 'PrismHR Print Settings Confirmation',
        message: ((revert
            ? 'Are you sure you want to <strong>revert</strong> the print settings back to <strong>standard</strong> for all client-customers?'
            : 'Are you sure you want to <strong>convert</strong> the print settings to <strong>PrismHR</strong> for all client-customers?')
          + '<br>This will alter the way Customers\' print documents are generated and delivered.')
      },

      disableClose: true
    }).afterClosed().subscribe((confirmed: boolean): void => {

      // Check for confirmation then save the print settings.
      if (confirmed) this.runSavePrintSettings(revert);
    });
  }

  /**
   * @description This method views the PEO Client Document Split Status.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#viewClientDocumentSplitStatus()
   * @param {PeoClientApiModel} client This parameter contains the client to view the document split status for.
   * @protected
   * @returns {void}
   */
  protected viewClientDocumentSplitStatus(client: PeoClientApiModel): void {

    // Open the dialog for the PEO Client Document Split Status.
    this.dialog.open(PeoClientDocumentSplitStatusAdminDialogComponent,
      { data: { client }, disableClose: true, width: '800px' });
  }

  /**
   * @description This method initializes the component.
   * @memberof PeoClientAdminPageComponent
   * @method
   * @name PeoClientAdminPageComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Set the unique identifier of the PEO Clients to load the clients for.
    this.peoId = this.route.snapshot.paramMap.has('peoId')
      ? this.route.snapshot.paramMap.get('peoId')?.toString() ?? undefined : undefined;

    // Load the PEO Clients.
    this.loadClients();
  }
}
